import type {
  IOpportunityLPCompanyTag,
  IOpportunityLPPersonTag,
  IOpportunityTag,
} from "@interfaces/tag.interface";
import { CompanyAvatar, CompanyAvatarSizes } from "@ui/Avatars";
import { TagLabel } from "@ui/Content";
import { Tooltip } from "@ui/Tooltip";
import { getFullNameOrEmail } from "@utils/users.util";

type Props = {
  item: IOpportunityTag;
} & React.HTMLAttributes<HTMLElement>;

const CHIP_CLASS =
  "inline-flex min-w-0 max-w-full items-center gap-1 rounded-[5px] bg-background-tag dark:bg-gray-750 p-[3px]";
const LABEL_CLASS =
  "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium text-content-primary dark:text-white";

/** LP chip label: truncates and shows the full text in a tooltip only when clipped. */
const LpLabel = ({ text }: { text: string }) => (
  <Tooltip
    placement="bottom-start"
    variant="dark"
    tooltipContent={text}
    tooltipContentClassName="max-w-[400px]"
    className="z-0 flex min-w-0 items-center"
    dependsOnTruncated
    delay={{ open: 500, close: 100 }}
    offset={4}
  >
    <div className={LABEL_CLASS}>{text}</div>
  </Tooltip>
);

const LpCompanyBadge = ({ lp }: { lp: IOpportunityLPCompanyTag }) => (
  <div className={CHIP_CLASS}>
    <CompanyAvatar size={CompanyAvatarSizes.SMALL} logoUrl={lp.logo_url} />
    <LpLabel text={lp.title} />
  </div>
);

const LpPersonBadge = ({ lp }: { lp: IOpportunityLPPersonTag }) => (
  <div className={CHIP_CLASS}>
    <CompanyAvatar
      size={CompanyAvatarSizes.SMALL}
      logoUrl={lp.logo}
      userProps={{ first_name: lp.first_name, last_name: lp.last_name }}
      classNames="rounded-full"
      showInitialsByDefault
    />
    <LpLabel text={getFullNameOrEmail(lp)} />
  </div>
);

export const SuggestionOpportunity = ({ item, ...rest }: Props) => {
  const lpTrailing = item.lp_company ? (
    <LpCompanyBadge lp={item.lp_company} />
  ) : item.lp_person ? (
    <LpPersonBadge lp={item.lp_person} />
  ) : null;

  return (
    <div {...rest} className="flex min-w-0 cursor-pointer items-center gap-2 p-2">
      <div className="flex min-w-0 items-center gap-2">
        <span
          aria-hidden
          className="inline-block h-2.5 w-2.5 flex-shrink-0 rotate-45 rounded-[1px]"
          style={{ backgroundColor: item.stage?.color }}
        />
        <TagLabel className="min-w-0 w-fit">{item.title}</TagLabel>
      </div>
      {lpTrailing}
    </div>
  );
};
